/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { noop, uniqueId } from 'lodash';

import type { InlineNotificationProps, PageNotificationProps } from '@coursera/cds-core';
import { Grid, InlineNotification, PageNotification } from '@coursera/cds-core';

type Commons = {
  id?: string;
  title?: string;
  addedAt?: number;
  namespace: string;
  dismissable?: boolean;
};

export type EnterpriseNotification =
  | (Commons & {
      severity?: InlineNotificationProps['severity'];
      children: InlineNotificationProps['children'];
      inlineAction?: InlineNotificationProps['inlineAction'];
      component?: 'InlineNotification';
    })
  | (Commons & {
      severity?: PageNotificationProps['severity'];
      children: PageNotificationProps['children'];
      inlineAction?: PageNotificationProps['inlineAction'];
      component: 'PageNotification';
    });

const NotificationsContext = React.createContext<{
  renderNotifications: (namespace: string, options?: { noMargin?: boolean }) => JSX.Element | null;
  addNotification: (notification: EnterpriseNotification) => void;
  clearNotifications: (namespace: string) => void;
}>({ renderNotifications: () => null, addNotification: noop, clearNotifications: noop });

export const Notifications = ({
  noMargin = false,
  notifications,
  onDismiss,
}: {
  noMargin?: boolean;
  notifications: EnterpriseNotification[];
  onDismiss?: (id: EnterpriseNotification['id']) => void;
}) => {
  if (!notifications.length) return null;
  return (
    <Grid
      container
      spacing={12}
      css={css`
        margin-bottom: ${noMargin ? 0 : 'var(--cds-spacing-300)'};
      `}
    >
      {notifications.map((notification) => (
        <Grid item xs={12} key={notification.id}>
          {notification.component === 'PageNotification' ? (
            <PageNotification
              role="alert"
              severity={notification.severity}
              inlineAction={notification.inlineAction}
              onDismiss={notification.dismissable && onDismiss ? () => onDismiss(notification.id) : undefined}
              title={notification.title}
            >
              {notification.children}
            </PageNotification>
          ) : (
            <InlineNotification
              role="alert"
              severity={notification.severity}
              inlineAction={notification.inlineAction}
              onDismiss={notification.dismissable && onDismiss ? () => onDismiss(notification.id) : undefined}
              title={notification.title}
            >
              {notification.children}
            </InlineNotification>
          )}
        </Grid>
      ))}
    </Grid>
  );
};
export const NotificationsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const [allNotifications, setAllNotifications] = React.useState<EnterpriseNotification[]>([]);

  const renderNotifications = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (namespace: string, options?: { noMargin?: boolean }) => {
      const staleCutOff = Date.now() - 10 * 1000; // 10 seconds ago
      const pageNotifications = allNotifications.filter(
        (_) => _.namespace === namespace && _.addedAt && _.addedAt > staleCutOff
      );
      return (
        <Notifications
          notifications={pageNotifications}
          onDismiss={(id) => setAllNotifications((all) => all.filter((_) => _.id !== id))}
          noMargin={options?.noMargin}
        />
      );
    },
    [allNotifications]
  );

  const addNotification = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (notification: EnterpriseNotification) =>
      setAllNotifications((_) => [..._, { dismissable: true, ...notification, addedAt: Date.now(), id: uniqueId() }]),
    []
  );
  const clearNotifications = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (namespace: string) =>
      setAllNotifications((notifications) => notifications.filter((_) => _.namespace !== namespace)),
    []
  );
  return (
    <NotificationsContext.Provider
      value={{
        renderNotifications,
        addNotification,
        clearNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

/**
 * This hook requires your app to be wrapped with NotificationsProvider
 *
 * it returns an object of the following shape: `{
 *  renderNotifications: (namespace: string) => JSX.Element | null;
 *  addNotification: (notification: Notification) => void;
 * }`
 *
 * call `{renderNotifications('SOME_NAMESPACE')}` to get a JSX.Element
 * and call `addNotification({namespace: 'SOME_NAMESPACE', children: _t('test'), severity: 'info' })`
 * to add a notification to be rendered
 *
 * see the NotificationsContext.test.tsx for more details
 */
export const useNotifications = () => {
  return React.useContext(NotificationsContext);
};
